import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from '@mui/material';
import { Facility, FREQUENCIES, REPAYMENT_TYPES, RepaymentType, ScheduleFrequency } from '../../interfaces/borrowing.interface';
import { BorrowingService } from '../../services/borrowing.service';
import { ExportFinanceService } from '../../services/exportFinance.service';
import SearchableSelect from '../common/SearchableSelect';
import { useToast } from '../../context/ToastContext';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';

const cell = { flex: '1 1 200px', maxWidth: 300 } as const;
const RETIRING = ['EDF', 'LTR', 'PAD', 'TIME_LOAN'];

export interface RetireTarget {
  acceptanceId: string; label: string; currencyCode: string; outstandingForeign: number;
}

/** Retire a B2B acceptance by a loan (W3-B5): creates the loan drawdown and the one retirement journal. */
const RetireByLoanDialog: React.FC<{ open: boolean; institutionId: string; target: RetireTarget | null;
  onClose: () => void; onDone: () => void }> = ({ open, institutionId, target, onClose, onDone }) => {
  const toast = useToast();
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [facilityId, setFacilityId] = useState('');
  const [f, setF] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let live = true;
    BorrowingService.listFacilities(institutionId)
      .then(r => { if (live) setFacilities(r.filter(x => RETIRING.includes(x.facilityType) && x.status === 'ACTIVE')); })
      .catch(err => toast.showApiError(err, 'Could not load the facilities'));
    setFacilityId('');
    setF({
      drawdownNo: '', date: toLocalIsoDate(), foreignAmount: target ? String(target.outstandingForeign) : '', loanRate: '',
      loanAmount: '', maturityDate: '', repaymentType: 'BULLET', frequency: 'MONTHLY', graceMonths: '0', installments: '',
      reference: '',
    });
    return () => { live = false; };
  }, [open, target, institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const facility = useMemo(() => facilities.find(x => x.id === facilityId) ?? null, [facilities, facilityId]);
  const foreignFacility = !!facility && !!facility.currencyCode && facility.currencyCode !== 'BDT';
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setF(s => ({ ...s, [k]: e.target.value }));
  const num = (v: string) => (v === '' ? null : Number(v));
  const needsInstalments = f.repaymentType !== 'BULLET';
  const valid = !!target && !!facility && f.drawdownNo?.trim() && f.date && Number(f.foreignAmount) > 0 &&
    (foreignFacility ? Number(f.loanRate) > 0 : Number(f.loanAmount) > 0) &&
    (needsInstalments ? !!f.installments : !!f.maturityDate);

  const save = async () => {
    if (!target || !facility) return;
    setBusy(true);
    try {
      await ExportFinanceService.retireB2bByLoan({
        institutionId, acceptanceId: target.acceptanceId, facilityId: facility.id, drawdownNo: f.drawdownNo.trim(),
        date: f.date, foreignAmount: Number(f.foreignAmount), loanRate: foreignFacility ? num(f.loanRate) : null,
        loanAmount: foreignFacility ? null : num(f.loanAmount), maturityDate: f.maturityDate || null,
        repaymentType: f.repaymentType as RepaymentType, frequency: needsInstalments ? (f.frequency as ScheduleFrequency) : null,
        graceMonths: num(f.graceMonths), installments: num(f.installments), reference: f.reference || null,
      });
      toast.showSuccess('Acceptance retired by the loan');
      onDone();
    } catch (err) {
      toast.showApiError(err, 'Could not retire the acceptance by a loan');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ 'data-help-page': 'fin.retire.page' } as any}>
      <DialogTitle>Retire by loan</DialogTitle>
      <DialogContent>
        {target && (
          <Typography variant="body2" sx={{ mb: 1 }} data-testid="retire-target">
            {target.label}: {formatMoney(target.outstandingForeign, target.currencyCode)} outstanding
          </Typography>
        )}
        <Alert severity="info" sx={{ mb: 2 }}>
          One journal settles the acceptance liability and books the loan (no cash moves). A retirement takes the amount in one
          loan; pay any remainder in cash.
        </Alert>
        <Box data-help-section="fin.retire.section.body" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={cell}>
            <SearchableSelect data-help="fin.retire.field.facility" options={facilities} value={facilityId} onChange={setFacilityId}
              getOptionLabel={x => `${x.facilityNo} - ${humanizeEnum(x.facilityType)} (${x.currencyCode || 'BDT'})`}
              label="Loan facility (EDF / LTR / PAD / time loan)" size="small" required />
          </Box>
          <TextField data-help="fin.retire.field.number" sx={cell} size="small" label="Drawdown no" required value={f.drawdownNo ?? ''} onChange={set('drawdownNo')} />
          <TextField data-help="fin.retire.field.date" sx={cell} size="small" type="date" label="Date" required InputLabelProps={{ shrink: true }} value={f.date ?? ''} onChange={set('date')} />
          <TextField data-help="fin.retire.field.amount" sx={cell} size="small" type="number" label={`Amount retired (${target?.currencyCode ?? ''})`} required
            value={f.foreignAmount ?? ''} onChange={set('foreignAmount')} />
          {foreignFacility ? (
            <TextField data-help="fin.retire.field.loanRate" sx={cell} size="small" type="number" label="Loan rate (BDT per unit)" required value={f.loanRate ?? ''} onChange={set('loanRate')} />
          ) : (
            <TextField data-help="fin.retire.field.loanAmount" sx={cell} size="small" type="number" label="Loan amount (BDT)" required value={f.loanAmount ?? ''} onChange={set('loanAmount')} />
          )}
          <TextField data-help="fin.retire.field.repayment" sx={cell} size="small" select label="Repayment" value={f.repaymentType ?? 'BULLET'} onChange={set('repaymentType')}>
            {REPAYMENT_TYPES.map(t => <MenuItem key={t} value={t}>{humanizeEnum(t)}</MenuItem>)}
          </TextField>
          {needsInstalments ? (
            <>
              <TextField data-help="fin.retire.field.frequency" sx={cell} size="small" select label="Frequency" value={f.frequency ?? 'MONTHLY'} onChange={set('frequency')}>
                {FREQUENCIES.map(t => <MenuItem key={t} value={t}>{humanizeEnum(t)}</MenuItem>)}
              </TextField>
              <TextField data-help="fin.retire.field.instalments" sx={cell} size="small" type="number" label="Instalments" required value={f.installments ?? ''} onChange={set('installments')} />
            </>
          ) : (
            <TextField data-help="fin.retire.field.maturity" sx={cell} size="small" type="date" label="Loan maturity" required InputLabelProps={{ shrink: true }}
              value={f.maturityDate ?? ''} onChange={set('maturityDate')} />
          )}
          <TextField data-help="fin.retire.field.reference" sx={cell} size="small" label="Reference" value={f.reference ?? ''} onChange={set('reference')} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button data-help="fin.retire.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="fin.retire.save" variant="contained" onClick={save} disabled={!valid || busy}>Retire by loan</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RetireByLoanDialog;
